import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Settings,
  Building2,
  Bell,
  CreditCard,
  Shield,
  Users,
  Mail,
  Phone,
  MapPin,
  Save,
  RefreshCw,
  Key,
  Clock,
  Check,
  Star,
  Zap,
  Crown,
  ArrowRight,
  X,
  Globe,
  FileText,
  BellRing,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  UserPlus,
  UserMinus,
  XCircle,
  Upload,
  Download,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  Edit,
  RotateCcw,
  ClipboardList,
  Trash2,
  Plus,
  DollarSign,
  HelpCircle,
  ExternalLink,
  Plug,
  GraduationCap,
} from "lucide-react";
import { toast } from "sonner";
import {
  AgencySettingsTab,
  NotificationSettingsTab,
  SystemSettingsTab,
  SecuritySettingsTab,
  HRSettingsTab,
  CalendarSettingsTab,
  StaffPermissionsTab,
  ServiceCodesSettingsTab,
  IntegrationsSettingsTab,
  FaxTemplatesSettingsTab,
  HelpSupportTab,
  InviteUserDialog,
  BulkImportDialog,
  CreateTemplateDialog,
  EditTemplateDialog,
  PasswordChangeDialog,
  RoleComparisonMatrixDialog,
} from "@/components/settings";
import { TermsModal } from "@/components/TermsModal";
import OrientationKitTab from "@/components/OrientationKitTab";
import FormTemplatesTab from "@/components/FormTemplatesTab";
import VisitRemindersTab from "@/components/settings/VisitRemindersTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SettingsPage() {
  const { user, subscriptionValid, subscriptionMessage, updateSubscriptionStatus } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "agency");
  
  // Agency Settings
  const [agencySettings, setAgencySettings] = useState({
    name: "",
    email: "",
    phone: "",
    fax: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    npi: "",
    tax_id: "",
    license_number: "",
    medicaid_provider_number: "",
    website: "",
  });

  // Notification Settings
  const [notificationSettings, setNotificationSettings] = useState({
    email_notifications: true,
    sms_notifications: false,
    appointment_reminders: true,
    reminder_hours_before: 24,
    daily_digest: false,
    urgent_alerts: true,
    referral_updates: true,
    document_expiry_alerts: true,
    evv_alerts: true,
    evv_flagged_alerts: true,
    evv_missed_alerts: true,
    evv_daily_summary: false,
  });

  // Test notification state
  const [testingNotification, setTestingNotification] = useState(false);

  // Subscription & Billing State
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  
  // Terms & Conditions
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [selectedPlanForUpgrade, setSelectedPlanForUpgrade] = useState(null);

  // Billing Settings
  const [billingSettings, setBillingSettings] = useState({
    billing_email: "",
    billing_address: "",
    default_rate: "",
    overtime_multiplier: "1.5",
    pay_period: "biweekly",
    invoice_prefix: "INV",
    auto_invoice: false,
  });

  // System Settings
  const [systemSettings, setSystemSettings] = useState({
    timezone: "America/Chicago",
    date_format: "MM/DD/YYYY",
    time_format: "12h",
    session_timeout: 60,
    require_2fa: false,
    allow_concurrent_sessions: true,
    audit_log_retention: 365,
  });

  // Calendar & Scheduling Settings
  const [schedulingSettings, setSchedulingSettings] = useState({
    calendar_display: "month", // "month" or "pay_period"
    show_pay_period_weeks: true,
    default_schedules_confirmed: false,
    show_service_code_description: true,
    show_payor_source: false,
    show_payroll_item: false,
    enable_notes_on_grid: true,
    show_caregiver_unavailable: true,
    show_task_completed_status: true,
    remove_client_phone: false,
    show_hours_on_grid: true,
    enable_quick_confirm: false,
    alert_caregiver_attributes_mismatch: true,
    enable_evv_conflict_alerts: true,
    enable_fte_alert: false,
    fte_alert_hours: 40,
  });
  const [savingSchedulingSettings, setSavingSchedulingSettings] = useState(false);

  // Password change
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [changingPassword, setChangingPassword] = useState(false);

  // User Management State
  const [agencyUsers, setAgencyUsers] = useState([]);
  const [usersSummary, setUsersSummary] = useState(null);
  const [inviteDialog, setInviteDialog] = useState(false);
  const [inviteData, setInviteData] = useState({ name: "", email: "", role: "pcw", phone: "" });
  const [inviting, setInviting] = useState(false);
  const [billingPreview, setBillingPreview] = useState(null);

  // Bulk Import State
  const [importDialog, setImportDialog] = useState(false);
  const [csvData, setCsvData] = useState("");
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Role Permissions State
  const [permissions, setPermissions] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [showComparisonMatrix, setShowComparisonMatrix] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(null);

  // Permission Templates
  const PERMISSION_TEMPLATES = {
    full_access: {
      name: "Full Access",
      description: "All permissions enabled - complete system access",
      icon: "🔓",
      color: "purple",
      permissions: {
        clients: ["create", "read", "update", "delete"],
        employees: ["create", "read", "update", "delete"],
        appointments: ["create", "read", "update", "delete"],
        evv: ["read", "verify", "edit", "clock_in", "clock_out"],
        billing: ["read", "update"],
        settings: ["read", "update"],
        users: ["create", "read", "update", "delete"],
        reports: ["read", "export"],
        evaluations: ["create", "read", "update", "delete"],
        communications: ["read", "send"],
        referrals: ["create", "read", "update", "delete"],
        visit_notes: ["create", "read", "update", "delete"]
      }
    },
    read_only: {
      name: "Read Only",
      description: "View-only access - no create, update, or delete",
      icon: "👁️",
      color: "slate",
      permissions: {
        clients: ["read"],
        employees: ["read"],
        appointments: ["read"],
        evv: ["read"],
        reports: ["read"],
        communications: ["read"],
        referrals: ["read"],
        visit_notes: ["read"]
      }
    },
    clinical_staff: {
      name: "Clinical Staff",
      description: "For RNs and clinical supervisors - patient care focus",
      icon: "🩺",
      color: "blue",
      permissions: {
        clients: ["read", "update"],
        employees: ["read"],
        appointments: ["read", "update"],
        evv: ["read", "verify"],
        evaluations: ["create", "read", "update"],
        communications: ["read", "send"],
        visit_notes: ["create", "read", "update"]
      }
    },
    care_provider: {
      name: "Care Provider",
      description: "For PCWs and caregivers - direct patient care",
      icon: "💊",
      color: "emerald",
      permissions: {
        clients: ["read"],
        appointments: ["read"],
        evv: ["clock_in", "clock_out"],
        visit_notes: ["create", "read"]
      }
    },
    administrative: {
      name: "Administrative",
      description: "For office staff - scheduling and communication",
      icon: "📋",
      color: "amber",
      permissions: {
        clients: ["create", "read", "update"],
        employees: ["read"],
        appointments: ["create", "read", "update"],
        communications: ["read", "send"],
        referrals: ["create", "read", "update"],
        reports: ["read"]
      }
    },
    intake_specialist: {
      name: "Intake Specialist",
      description: "For intake coordinators - referrals and new clients",
      icon: "📥",
      color: "pink",
      permissions: {
        clients: ["create", "read", "update"],
        referrals: ["create", "read", "update"],
        appointments: ["create", "read"],
        communications: ["read", "send"]
      }
    }
  };

  // Custom Templates State
  const [customTemplates, setCustomTemplates] = useState([]);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: "",
    description: "",
    icon: "⚙️",
    color: "slate",
    permissions: {}
  });

  // Available icons and colors for custom templates
  const TEMPLATE_ICONS = ["⚙️", "🔧", "🛡️", "🎯", "⭐", "🔑", "📊", "💼", "🏥", "👥", "📝", "✨"];
  const TEMPLATE_COLORS = [
    { id: "slate", label: "Gray", class: "bg-slate-100 border-slate-300" },
    { id: "blue", label: "Blue", class: "bg-blue-100 border-blue-300" },
    { id: "emerald", label: "Green", class: "bg-emerald-100 border-emerald-300" },
    { id: "amber", label: "Orange", class: "bg-amber-100 border-amber-300" },
    { id: "pink", label: "Pink", class: "bg-pink-100 border-pink-300" },
    { id: "purple", label: "Purple", class: "bg-purple-100 border-purple-300" },
    { id: "red", label: "Red", class: "bg-red-100 border-red-300" },
    { id: "cyan", label: "Cyan", class: "bg-cyan-100 border-cyan-300" }
  ];

  // Permission categories for template builder
  const PERMISSION_CATEGORIES = {
    clients: { label: "Clients", actions: ["create", "read", "update", "delete"] },
    employees: { label: "Employees", actions: ["create", "read", "update", "delete"] },
    appointments: { label: "Appointments", actions: ["create", "read", "update", "delete"] },
    evv: { label: "EVV", actions: ["read", "verify", "edit", "clock_in", "clock_out"] },
    evaluations: { label: "Evaluations", actions: ["create", "read", "update", "delete"] },
    communications: { label: "Communications", actions: ["read", "send"] },
    referrals: { label: "Referrals", actions: ["create", "read", "update", "delete"] },
    reports: { label: "Reports", actions: ["read", "export"] },
    visit_notes: { label: "Visit Notes", actions: ["create", "read", "update", "delete"] },
    billing: { label: "Billing", actions: ["read", "update"] },
    settings: { label: "Settings", actions: ["read", "update"] },
    users: { label: "Users", actions: ["create", "read", "update", "delete"] }
  };

  // Role Change State
  const [changingRoleFor, setChangingRoleFor] = useState(null);

  // Activity Log State
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityFilter, setActivityFilter] = useState("all");
  const [exportDays, setExportDays] = useState(30);

  // Activity Notification Settings State
  const [activityNotifSettings, setActivityNotifSettings] = useState(null);
  const [savingNotifSettings, setSavingNotifSettings] = useState(false);

  // Evaluation Reminder Settings State
  const [evalReminderSettings, setEvalReminderSettings] = useState({
    enabled: false,
    first_reminder_days: 3,
    subsequent_reminder_days: 7,
    max_reminders: 3,
    reminder_email_subject: "Reminder: Please Review Your Performance Evaluation",
    include_link_in_reminder: true
  });
  const [savingEvalReminders, setSavingEvalReminders] = useState(false);
  const [checkingReminders, setCheckingReminders] = useState(false);

  useEffect(() => {
    if (user?.role !== "admin") {
      toast.error("Access denied. Administrator only.");
      navigate("/dashboard");
      return;
    }
    fetchSettings();
    fetchBillingData();
    fetchUsers();
    fetchPermissions();
    fetchActivityLogs();
    fetchActivityNotifSettings();
    fetchEvalReminderSettings();
    
    // Check for payment return from Stripe
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [user, navigate, searchParams]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/settings`);
      const settings = response.data;
      
      if (settings.agency) setAgencySettings(prev => ({ ...prev, ...settings.agency }));
      if (settings.notifications) setNotificationSettings(prev => ({ ...prev, ...settings.notifications }));
      if (settings.billing) setBillingSettings(prev => ({ ...prev, ...settings.billing }));
      if (settings.system) setSystemSettings(prev => ({ ...prev, ...settings.system }));
      if (settings.scheduling) setSchedulingSettings(prev => ({ ...prev, ...settings.scheduling }));
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingData = async () => {
    try {
      const [plansRes, subRes, txnRes] = await Promise.all([
        axios.get(`${API}/billing/plans`),
        axios.get(`${API}/billing/subscription`),
        axios.get(`${API}/billing/transactions`),
      ]);
      setPlans(plansRes.data);
      setSubscription(subRes.data);
      setTransactions(txnRes.data);
    } catch (error) {
      console.error("Failed to fetch billing data:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setAgencyUsers(response.data.users || []);
      setUsersSummary(response.data.summary || null);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const fetchBillingPreview = async () => {
    try {
      const response = await axios.get(`${API}/users/billing-preview`);
      setBillingPreview(response.data);
    } catch (error) {
      console.error("Failed to fetch billing preview:", error);
    }
  };

  const handleInviteUser = async () => {
    if (!inviteData.name || !inviteData.email) {
      toast.error("Name and email are required");
      return;
    }
    
    setInviting(true);
    try {
      const response = await axios.post(`${API}/users/invite`, inviteData);
      toast.success(`User invited! Temporary password: ${response.data.temp_password}`, { duration: 10000 });
      setInviteDialog(false);
      setInviteData({ name: "", email: "", role: "pcw", phone: "" });
      fetchUsers();
      fetchBillingData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to invite user");
    } finally {
      setInviting(false);
    }
  };

  const handleDeactivateUser = async (userId, userName) => {
    if (!confirm(`Are you sure you want to deactivate ${userName}? They will no longer be able to log in.`)) {
      return;
    }
    
    try {
      await axios.patch(`${API}/users/${userId}/deactivate`);
      toast.success(`${userName} has been deactivated`);
      fetchUsers();
      fetchBillingData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to deactivate user");
    }
  };

  const handleReactivateUser = async (userId, userName) => {
    try {
      await axios.patch(`${API}/users/${userId}/reactivate`);
      toast.success(`${userName} has been reactivated`);
      fetchUsers();
      fetchBillingData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reactivate user");
    }
  };

  // Role Change Handler
  const handleChangeRole = async (userId, newRole, userName) => {
    setChangingRoleFor(userId);
    try {
      const response = await axios.patch(`${API}/users/${userId}/role`, { role: newRole });
      toast.success(`Role updated for ${userName}: ${formatRoleName(response.data.old_role)} → ${formatRoleName(response.data.new_role)}`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change role");
    } finally {
      setChangingRoleFor(null);
    }
  };

  const formatRoleName = (role) => {
    const names = {
      admin: "Administrator",
      case_manager: "Case Manager",
      intake_coordinator: "Intake Coordinator",
      receptionist: "Receptionist",
      rn: "RN",
      pcw: "PCW"
    };
    return names[role] || role;
  };

  // Bulk Import Handlers
  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/users/import/template`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'user_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const handlePreviewImport = async () => {
    if (!csvData.trim()) {
      toast.error("Please paste CSV data");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/users/import/preview`, { csv_data: csvData });
      setImportPreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to preview import");
    }
  };

  const handleExecuteImport = async () => {
    if (!importPreview || importPreview.valid_count === 0) {
      toast.error("No valid users to import");
      return;
    }
    
    setImporting(true);
    try {
      const response = await axios.post(`${API}/users/import`, { csv_data: csvData });
      setImportResult(response.data);
      toast.success(`Successfully imported ${response.data.imported_users.length} users`);
      fetchUsers();
      fetchBillingData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import users");
    } finally {
      setImporting(false);
    }
  };

  // Permission Handlers
  const fetchPermissions = async () => {
    try {
      const response = await axios.get(`${API}/roles`);
      // Transform data to flatten permissions structure for easier UI access
      const roles = response.data.roles || {};
      const flattenedPermissions = {};
      for (const [roleId, roleData] of Object.entries(roles)) {
        flattenedPermissions[roleId] = roleData.permissions || {};
      }
      setPermissions({
        permissions: flattenedPermissions,
        roleInfo: roles, // Keep original for names/descriptions
        is_custom: false
      });
      // Fetch custom templates after permissions are loaded
      fetchCustomTemplates();
    } catch (error) {
      console.error("Failed to fetch permissions:", error);
    }
  };

  const fetchActivityLogs = async () => {
    try {
      const params = activityFilter !== "all" 
        ? `?action_type=${activityFilter}&limit=50` 
        : "?limit=50";
      const response = await axios.get(`${API}/activity-logs${params}`);
      setActivityLogs(response.data.logs || []);
    } catch (error) {
      console.error("Failed to fetch activity logs:", error);
    }
  };

  const formatActivityTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getActivityIcon = (actionType) => {
    const icons = {
      login: "🔐",
      logout: "🚪",
      user_invited: "📨",
      user_deactivated: "🚫",
      user_reactivated: "✅",
      role_changed: "🔄",
      password_changed: "🔑",
      settings_updated: "⚙️",
      permissions_updated: "🛡️",
      subscription_changed: "💳",
      bulk_import: "📥",
    };
    return icons[actionType] || "📋";
  };

  // Scheduling Settings Handlers
  const fetchSchedulingSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/scheduling`);
      if (response.data) {
        setSchedulingSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error("Failed to fetch scheduling settings:", error);
    }
  };

  const handleSaveSchedulingSettings = async () => {
    setSavingSchedulingSettings(true);
    try {
      await axios.put(`${API}/settings/scheduling`, schedulingSettings);
      toast.success("Calendar & Scheduling settings saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save scheduling settings");
    } finally {
      setSavingSchedulingSettings(false);
    }
  };

  // Activity Notification Settings Handlers
  const fetchActivityNotifSettings = async () => {
    try {
      const response = await axios.get(`${API}/activity-notifications/settings`);
      setActivityNotifSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch activity notification settings:", error);
    }
  };

  const fetchEvalReminderSettings = async () => {
    try {
      const response = await axios.get(`${API}/evaluation-reminder-settings`);
      setEvalReminderSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch evaluation reminder settings:", error);
    }
  };

  const handleSaveEvalReminderSettings = async () => {
    setSavingEvalReminders(true);
    try {
      await axios.put(`${API}/evaluation-reminder-settings`, evalReminderSettings);
      toast.success("Evaluation reminder settings saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save reminder settings");
    } finally {
      setSavingEvalReminders(false);
    }
  };

  const handleCheckReminders = async () => {
    setCheckingReminders(true);
    try {
      const response = await axios.post(`${API}/evaluation-reminders/check`);
      if (response.data.reminders_sent > 0) {
        toast.success(`${response.data.reminders_sent} reminder(s) sent`);
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to check reminders");
    } finally {
      setCheckingReminders(false);
    }
  };

  const handleSaveNotifSettings = async () => {
    if (!activityNotifSettings) return;
    
    setSavingNotifSettings(true);
    try {
      await axios.put(`${API}/activity-notifications/settings`, activityNotifSettings);
      toast.success("Notification settings saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save notification settings");
    } finally {
      setSavingNotifSettings(false);
    }
  };

  const handleExportActivityLogs = async (format = "csv") => {
    try {
      const params = activityFilter !== "all" 
        ? `?format=${format}&action_type=${activityFilter}&days=${exportDays}`
        : `?format=${format}&days=${exportDays}`;
      
      const response = await axios.get(`${API}/activity-logs/export${params}`, {
        responseType: format === "csv" ? "blob" : "json"
      });
      
      if (format === "csv") {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `activity_log_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success("Activity log exported");
      } else {
        // JSON export - could open in new tab or download
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: "application/json" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `activity_log_${new Date().toISOString().split('T')[0]}.json`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success("Activity log exported");
      }
    } catch (error) {
      toast.error("Failed to export activity logs");
    }
  };

  const handleSavePermissions = async (role, newPermissions) => {
    setSavingPermissions(true);
    try {
      await axios.put(`${API}/roles/${role}`, { permissions: newPermissions });
      toast.success(`Permissions updated for ${formatRoleName(role)}`);
      fetchPermissions();
      setEditingRole(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save permissions");
    } finally {
      setSavingPermissions(false);
    }
  };

  // Fetch custom templates
  const fetchCustomTemplates = async () => {
    try {
      const response = await axios.get(`${API}/permission-templates`);
      setCustomTemplates(response.data || []);
    } catch (error) {
      console.error("Failed to fetch custom templates:", error);
    }
  };

  // Create custom template
  const handleCreateTemplate = async () => {
    if (!newTemplate.name.trim()) {
      toast.error("Template name is required");
      return;
    }
    if (Object.keys(newTemplate.permissions).length === 0) {
      toast.error("Please select at least one permission");
      return;
    }

    setSavingTemplate(true);
    try {
      await axios.post(`${API}/permission-templates`, newTemplate);
      toast.success(`Template "${newTemplate.name}" created successfully`);
      fetchCustomTemplates();
      setShowCreateTemplate(false);
      setNewTemplate({ name: "", description: "", icon: "⚙️", color: "slate", permissions: {} });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create template");
    } finally {
      setSavingTemplate(false);
    }
  };

  // Update custom template
  const handleUpdateTemplate = async () => {
    if (!editingTemplate) return;
    
    setSavingTemplate(true);
    try {
      await axios.put(`${API}/permission-templates/${editingTemplate.id}`, {
        name: editingTemplate.name,
        description: editingTemplate.description,
        icon: editingTemplate.icon,
        color: editingTemplate.color,
        permissions: editingTemplate.permissions
      });
      toast.success(`Template "${editingTemplate.name}" updated`);
      fetchCustomTemplates();
      setEditingTemplate(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update template");
    } finally {
      setSavingTemplate(false);
    }
  };

  // Delete custom template
  const handleDeleteTemplate = async (templateId, templateName) => {
    if (!confirm(`Delete template "${templateName}"? This cannot be undone.`)) return;
    
    try {
      await axios.delete(`${API}/permission-templates/${templateId}`);
      toast.success(`Template "${templateName}" deleted`);
      fetchCustomTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete template");
    }
  };

  // Apply custom template to a role
  const handleApplyCustomTemplate = async (role, template) => {
    if (!confirm(`Apply "${template.name}" template to ${formatRoleName(role)}? This will replace all current permissions.`)) {
      return;
    }
    
    setApplyingTemplate(role);
    try {
      await axios.put(`${API}/roles/${role}`, { permissions: template.permissions });
      toast.success(`"${template.name}" template applied to ${formatRoleName(role)}`);
      fetchPermissions();
      setEditingRole(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply template");
    } finally {
      setApplyingTemplate(null);
    }
  };

  // Toggle permission in template builder
  const toggleTemplatePermission = (category, action, isEditing = false) => {
    const targetTemplate = isEditing ? editingTemplate : newTemplate;
    const setTemplate = isEditing ? setEditingTemplate : setNewTemplate;
    
    const currentPerms = { ...targetTemplate.permissions };
    if (!currentPerms[category]) {
      currentPerms[category] = [];
    }
    
    if (currentPerms[category].includes(action)) {
      currentPerms[category] = currentPerms[category].filter(a => a !== action);
      if (currentPerms[category].length === 0) {
        delete currentPerms[category];
      }
    } else {
      currentPerms[category] = [...currentPerms[category], action];
    }
    
    setTemplate({ ...targetTemplate, permissions: currentPerms });
  };

  // Apply permission template to a role (built-in templates)
  const handleApplyTemplate = async (role, templateId) => {
    const template = PERMISSION_TEMPLATES[templateId];
    if (!template) return;
    
    if (!confirm(`Apply "${template.name}" template to ${formatRoleName(role)}? This will replace all current permissions.`)) {
      return;
    }
    
    setApplyingTemplate(role);
    try {
      await axios.put(`${API}/roles/${role}`, { permissions: template.permissions });
      toast.success(`"${template.name}" template applied to ${formatRoleName(role)}`);
      fetchPermissions();
      setEditingRole(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply template");
    } finally {
      setApplyingTemplate(null);
    }
  };

  const handleResetPermissions = async () => {
    if (!confirm("Reset all role permissions to defaults? This cannot be undone.")) return;
    
    try {
      // Reset each role individually
      const roles = ["case_manager", "rn", "pcw", "intake_coordinator", "receptionist"];
      for (const role of roles) {
        await axios.post(`${API}/roles/reset/${role}`);
      }
      toast.success("Permissions reset to defaults");
      fetchPermissions();
    } catch (error) {
      toast.error("Failed to reset permissions");
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      toast.error("Payment status check timed out. Please refresh the page.");
      return;
    }

    setCheckingPayment(true);
    try {
      const response = await axios.get(`${API}/billing/checkout/status/${sessionId}`);
      
      if (response.data.status === "paid") {
        toast.success("Payment successful! Your subscription has been upgraded.");
        updateSubscriptionStatus(true, null); // Update auth context
        fetchBillingData();
        // Clear the URL params
        navigate("/settings?tab=billing", { replace: true });
        setCheckingPayment(false);
        return;
      } else if (response.data.status === "expired") {
        toast.error("Payment session expired. Please try again.");
        setCheckingPayment(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error("Error checking payment status:", error);
      toast.error("Error checking payment status");
      setCheckingPayment(false);
    }
  };

  const handleUpgradePlan = async (planId) => {
    // Show terms modal before proceeding to checkout
    const selectedPlan = plans.find(p => p.id === planId);
    setSelectedPlanForUpgrade({ id: planId, name: selectedPlan?.name || planId });
    setTermsModalOpen(true);
  };

  const proceedToCheckout = async (couponCode) => {
    const planId = selectedPlanForUpgrade?.id;
    if (!planId) return;

    setUpgrading(true);
    try {
      // Record terms acceptance
      await axios.post(`${API}/billing/accept-terms`, {
        plan_id: planId,
        accepted_at: new Date().toISOString()
      });

      const checkoutData = {
        plan_id: planId,
        origin_url: window.location.origin,
        return_path: "/settings?tab=billing",
      };
      
      // Add coupon code if provided
      if (couponCode) {
        checkoutData.coupon_code = couponCode;
      }

      const response = await axios.post(`${API}/billing/checkout`, checkoutData);
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      const msg = error.response?.data?.detail || error.message || "Checkout failed. Please try again.";
      toast.error(msg);
    } finally {
      setUpgrading(false);
      setTermsModalOpen(false);
    }
  };

  const handleSaveAgencySettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/agency`, agencySettings);
      toast.success("Agency settings saved!");
    } catch (error) {
      console.error("Failed to save agency settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotificationSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/notifications`, notificationSettings);
      toast.success("Notification settings saved!");
    } catch (error) {
      console.error("Failed to save notification settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBillingSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/billing`, billingSettings);
      toast.success("Billing settings saved!");
    } catch (error) {
      console.error("Failed to save billing settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSystemSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/system`, systemSettings);
      toast.success("System settings saved!");
    } catch (error) {
      console.error("Failed to save system settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }
    if (passwordData.new_password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });
      toast.success("Password changed successfully!");
      setPasswordDialog(false);
      setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      console.error("Failed to change password:", error);
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  if (user?.role !== "admin") {
    return null;
  }

  return (
    <Layout>
      {/* Subscription Required Banner */}
      {!subscriptionValid && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-amber-800">Subscription Required</h3>
              <p className="text-amber-700 text-sm mt-1">
                {subscriptionMessage || "Please subscribe to a plan to access all features of CarePilot."}
              </p>
              <p className="text-amber-600 text-sm mt-2">
                Select a plan below to get started. You will need to agree to the Terms & Conditions before completing your subscription.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Settings className="w-6 h-6" />
            Settings
          </h1>
          <p className="text-slate-500">Manage your agency settings and preferences</p>
        </div>
        <Button
          variant="outline"
          className="gap-2"
          onClick={() => window.location.href = "mailto:support@carepilotusa.com?subject=CarePilot Support Request"}
          data-testid="help-button"
        >
          <HelpCircle className="w-4 h-4" />
          Get Help
          <ExternalLink className="w-3 h-3 text-slate-400" />
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="w-8 h-8 mx-auto animate-spin text-primary" />
          <p className="mt-4 text-slate-500">Loading settings...</p>
        </div>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-slate-200 flex-wrap h-auto p-1">
            <TabsTrigger value="agency" className="gap-2" data-testid="tab-agency">
              <Building2 className="w-4 h-4" />
              Agency
            </TabsTrigger>
            <TabsTrigger value="notifications" className="gap-2" data-testid="tab-notifications">
              <Bell className="w-4 h-4" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="billing" className="gap-2" data-testid="tab-billing">
              <CreditCard className="w-4 h-4" />
              Billing
            </TabsTrigger>
            <TabsTrigger value="calendar" className="gap-2" data-testid="tab-calendar">
              <Clock className="w-4 h-4" />
              Calendar & Scheduling
            </TabsTrigger>
            <TabsTrigger value="system" className="gap-2" data-testid="tab-system">
              <Shield className="w-4 h-4" />
              System
            </TabsTrigger>
            <TabsTrigger value="security" className="gap-2" data-testid="tab-security">
              <Key className="w-4 h-4" />
              Security
            </TabsTrigger>
            <TabsTrigger value="users" className="gap-2" data-testid="tab-users">
              <Users className="w-4 h-4" />
              Users
            </TabsTrigger>
            <TabsTrigger value="permissions" className="gap-2" data-testid="tab-permissions">
              <Lock className="w-4 h-4" />
              Staff Permissions
            </TabsTrigger>
            <TabsTrigger value="hr" className="gap-2" data-testid="tab-hr">
              <ClipboardList className="w-4 h-4" />
              HR Settings
            </TabsTrigger>
            <TabsTrigger value="service-codes" className="gap-2" data-testid="tab-service-codes">
              <DollarSign className="w-4 h-4" />
              Service Codes
            </TabsTrigger>
            <TabsTrigger value="integrations" className="gap-2" data-testid="tab-integrations">
              <Plug className="w-4 h-4" />
              Integrations
            </TabsTrigger>
            <TabsTrigger value="fax-templates" className="gap-2" data-testid="tab-fax-templates">
              <FileText className="w-4 h-4" />
              Fax Templates
            </TabsTrigger>
            <TabsTrigger value="orientation-kit" className="gap-2" data-testid="tab-orientation-kit">
              <GraduationCap className="w-4 h-4" />
              Onboarding Kit
            </TabsTrigger>
            <TabsTrigger value="form-templates" className="gap-2" data-testid="tab-form-templates">
              <FileText className="w-4 h-4" />
              Form Templates
            </TabsTrigger>
            <TabsTrigger value="visit-reminders" className="gap-2" data-testid="tab-visit-reminders">
              <BellRing className="w-4 h-4" />
              Visit Reminders
            </TabsTrigger>
            <TabsTrigger value="help" className="gap-2" data-testid="tab-help">
              <HelpCircle className="w-4 h-4" />
              Help & Support
            </TabsTrigger>
          </TabsList>

          {/* Agency Settings Tab */}
          <TabsContent value="agency">
            <AgencySettingsTab
              settings={agencySettings}
              onChange={setAgencySettings}
              onSave={handleSaveAgencySettings}
              saving={saving}
              agencyCode={user?.agency_code}
            />
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <NotificationSettingsTab
              settings={notificationSettings}
              onChange={setNotificationSettings}
              onSave={handleSaveNotificationSettings}
              onTestNotification={async () => {
                setTestingNotification(true);
                try {
                  await axios.post(`${API}/notifications/test?notification_type=email`);
                  toast.success("Test notification sent! (MOCKED - check notification history)");
                } catch (error) {
                  toast.error("Failed to send test notification");
                } finally {
                  setTestingNotification(false);
                }
              }}
              saving={saving}
              testingNotification={testingNotification}
            />
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing">
            <div className="space-y-6">
              {/* Current Subscription */}
              {subscription && (
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h2 className="text-lg font-semibold text-slate-900 mb-4">Current Subscription</h2>
                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg border border-primary/20">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                        {subscription.plan_id === "simple" && <Star className="w-6 h-6 text-primary" />}
                        {subscription.plan_id === "pro" && <Zap className="w-6 h-6 text-primary" />}
                        {subscription.plan_id === "enterprise" && <Crown className="w-6 h-6 text-primary" />}
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{subscription.plan_name}</h3>
                        <p className="text-sm text-slate-600">
                          {subscription.price > 0 
                            ? `$${subscription.price}/${subscription.interval}` 
                            : "Contact for pricing"}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        subscription.status === "active" 
                          ? "bg-emerald-100 text-emerald-700" 
                          : "bg-amber-100 text-amber-700"
                      }`}>
                        {subscription.status?.toUpperCase()}
                      </span>
                      {subscription.next_billing_date && (
                        <p className="text-xs text-slate-500 mt-1">
                          Next billing: {subscription.next_billing_date.split("T")[0]}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Current Features */}
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
                    {subscription.features?.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                        <Check className="w-4 h-4 text-emerald-500" />
                        {feature}
                      </div>
                    ))}
                  </div>

                  {/* User Billing Summary */}
                  {subscription.user_billing && (
                    <div className="mt-6 pt-6 border-t border-slate-200">
                      <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        User Billing Summary
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-xs text-slate-500">Total Users</p>
                          <p className="text-lg font-semibold text-slate-900">{subscription.user_billing.total_users}</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-xs text-slate-500">Included</p>
                          <p className="text-lg font-semibold text-emerald-600">{subscription.user_billing.included_users}</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-xs text-slate-500">Additional Users</p>
                          <p className="text-lg font-semibold text-slate-900">
                            {subscription.user_billing.additional_users}
                            {subscription.user_billing.additional_users > 0 && (
                              <span className="text-xs text-slate-500 ml-1">
                                (+${subscription.user_billing.additional_users_cost.toFixed(2)})
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="p-3 bg-primary/10 rounded-lg border border-primary/20">
                          <p className="text-xs text-primary">Total Monthly</p>
                          <p className="text-lg font-bold text-primary">${subscription.user_billing.total_monthly.toFixed(2)}</p>
                        </div>
                      </div>
                      {subscription.user_billing.additional_users > 0 && (
                        <p className="mt-2 text-xs text-slate-500">
                          * Each additional user beyond {subscription.user_billing.included_users} included costs ${subscription.user_billing.additional_user_price}/month
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Payment Processing Status */}
              {checkingPayment && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
                  <RefreshCw className="w-8 h-8 mx-auto text-blue-600 animate-spin mb-2" />
                  <p className="text-blue-800 font-medium">Processing your payment...</p>
                  <p className="text-sm text-blue-600">Please wait while we verify your payment.</p>
                </div>
              )}

              {/* Subscription Plans */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-2">Subscription Plans</h2>
                <p className="text-sm text-slate-500 mb-6">Choose the plan that best fits your agency</p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {plans.map((plan) => (
                    <div 
                      key={plan.id} 
                      className={`relative rounded-xl border-2 p-6 transition-all ${
                        subscription?.plan_id === plan.id 
                          ? "border-primary bg-primary/5" 
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      {plan.id === "growth" && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                          <span className="px-3 py-1 bg-primary text-white text-xs font-medium rounded-full">
                            POPULAR
                          </span>
                        </div>
                      )}
                      
                      <div className="text-center mb-4">
                        <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3">
                          {plan.id === "starter" && <Star className="w-6 h-6 text-slate-600" />}
                          {plan.id === "growth" && <Zap className="w-6 h-6 text-primary" />}
                          {plan.id === "enterprise" && <Crown className="w-6 h-6 text-amber-500" />}
                        </div>
                        <h3 className="font-semibold text-slate-900">{plan.name}</h3>
                        {plan.client_range && (
                          <p className="text-xs text-slate-500 mt-1">{plan.client_range}</p>
                        )}
                        <div className="mt-2">
                          <span className="text-3xl font-bold text-slate-900">
                            ${plan.price}
                          </span>
                          {plan.price > 0 && (
                            <span className="text-slate-500">/{plan.interval || "month"}</span>
                          )}
                        </div>
                        {plan.included_users && (
                          <p className="text-xs text-primary mt-1">
                            {plan.included_users} users included • +${plan.additional_user_price}/extra user
                          </p>
                        )}
                      </div>
                      
                      <ul className="space-y-2 mb-6">
                        {plan.features?.map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                            <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      
                      {subscription?.plan_id === plan.id ? (
                        <Button disabled className="w-full" variant="outline">
                          Current Plan
                        </Button>
                      ) : (
                        <Button 
                          className="w-full gap-2"
                          variant={plan.id === "growth" ? "default" : "outline"}
                          onClick={() => handleUpgradePlan(plan.id)}
                          disabled={upgrading}
                        >
                          {upgrading ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              Select Plan
                              <ArrowRight className="w-4 h-4" />
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Payment History */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Payment History</h2>
                {transactions.length === 0 ? (
                  <p className="text-center py-8 text-slate-500">No payment history yet</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Plan</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {transactions.map((txn) => (
                          <TableRow key={txn.id}>
                            <TableCell>{txn.created_at?.split("T")[0]}</TableCell>
                            <TableCell>{txn.plan_name}</TableCell>
                            <TableCell>${txn.amount?.toFixed(2)} {txn.currency?.toUpperCase()}</TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                txn.payment_status === "paid" 
                                  ? "bg-emerald-100 text-emerald-700"
                                  : txn.payment_status === "pending" || txn.payment_status === "initiated"
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-red-100 text-red-700"
                              }`}>
                                {txn.payment_status?.toUpperCase()}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>

              {/* Billing Settings */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Billing Preferences</h2>
                    <p className="text-sm text-slate-500">Configure billing preferences and rates</p>
                  </div>
                  <Button onClick={handleSaveBillingSettings} disabled={saving} className="gap-2">
                    <Save className="w-4 h-4" />
                    {saving ? "Saving..." : "Save Changes"}
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Billing Email</Label>
                      <Input
                        type="email"
                        value={billingSettings.billing_email}
                        onChange={(e) => setBillingSettings(prev => ({ ...prev, billing_email: e.target.value }))}
                        placeholder="billing@agency.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Billing Address</Label>
                      <Textarea
                        value={billingSettings.billing_address}
                        onChange={(e) => setBillingSettings(prev => ({ ...prev, billing_address: e.target.value }))}
                        placeholder="Enter billing address..."
                        rows={3}
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Default Hourly Rate ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={billingSettings.default_rate}
                          onChange={(e) => setBillingSettings(prev => ({ ...prev, default_rate: e.target.value }))}
                          placeholder="25.00"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Overtime Multiplier</Label>
                        <Select
                          value={billingSettings.overtime_multiplier}
                          onValueChange={(val) => setBillingSettings(prev => ({ ...prev, overtime_multiplier: val }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1.25">1.25x</SelectItem>
                            <SelectItem value="1.5">1.5x</SelectItem>
                            <SelectItem value="2">2x</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Pay Period</Label>
                      <Select
                        value={billingSettings.pay_period}
                        onValueChange={(val) => setBillingSettings(prev => ({ ...prev, pay_period: val }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="biweekly">Bi-Weekly</SelectItem>
                          <SelectItem value="semimonthly">Semi-Monthly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Calendar & Scheduling Tab */}
          <TabsContent value="calendar">
            <CalendarSettingsTab
              settings={schedulingSettings}
              onChange={setSchedulingSettings}
              onSave={handleSaveSchedulingSettings}
              saving={savingSchedulingSettings}
            />
          </TabsContent>

          {/* System Tab */}
          <TabsContent value="system">
            <SystemSettingsTab
              settings={systemSettings}
              onChange={setSystemSettings}
              onSave={handleSaveSystemSettings}
              saving={saving}
            />
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security">
            <SecuritySettingsTab
              settings={systemSettings}
              onChange={setSystemSettings}
              onChangePassword={() => setPasswordDialog(true)}
            />
          </TabsContent>

          {/* Users Management Tab */}
          <TabsContent value="users">
            <div className="space-y-6">
              {/* Users Summary */}
              {usersSummary && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <p className="text-xs text-slate-500">Total Users</p>
                    <p className="text-2xl font-bold text-slate-900">{usersSummary.total_users}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <p className="text-xs text-slate-500">Active</p>
                    <p className="text-2xl font-bold text-emerald-600">{usersSummary.active_users}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <p className="text-xs text-slate-500">Included in Plan</p>
                    <p className="text-2xl font-bold text-blue-600">{usersSummary.included_users}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <p className="text-xs text-slate-500">Additional Users</p>
                    <p className="text-2xl font-bold text-amber-600">{usersSummary.additional_users}</p>
                  </div>
                  <div className="bg-primary/10 rounded-xl border border-primary/20 p-4">
                    <p className="text-xs text-primary">Extra Monthly Cost</p>
                    <p className="text-2xl font-bold text-primary">${usersSummary.additional_users_cost?.toFixed(2) || "0.00"}</p>
                  </div>
                </div>
              )}

              {/* Users List */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Team Members</h2>
                    <p className="text-sm text-slate-500">Manage users who can access your agency</p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      onClick={() => { setImportDialog(true); setImportPreview(null); setImportResult(null); setCsvData(""); }}
                      className="gap-2"
                      data-testid="bulk-import-btn"
                    >
                      <Upload className="w-4 h-4" />
                      Bulk Import
                    </Button>
                    <Button 
                      onClick={() => { setInviteDialog(true); fetchBillingPreview(); }} 
                      className="gap-2"
                      data-testid="invite-user-btn"
                    >
                      <UserPlus className="w-4 h-4" />
                      Invite User
                    </Button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Billing</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {agencyUsers.map((u) => (
                        <TableRow key={u.id} className={!u.is_active ? "opacity-60" : ""}>
                          <TableCell className="font-medium">
                            {u.name}
                            {u.id === user?.id && (
                              <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">You</span>
                            )}
                          </TableCell>
                          <TableCell>{u.email}</TableCell>
                          <TableCell>
                            {u.id === user?.id ? (
                              // Current user - show badge only (can't change own role)
                              <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                                u.role === "admin" ? "bg-purple-100 text-purple-700" :
                                u.role === "rn" ? "bg-blue-100 text-blue-700" :
                                u.role === "case_manager" ? "bg-emerald-100 text-emerald-700" :
                                u.role === "intake_coordinator" ? "bg-amber-100 text-amber-700" :
                                u.role === "receptionist" ? "bg-pink-100 text-pink-700" :
                                "bg-slate-100 text-slate-700"
                              }`}>
                                {formatRoleName(u.role)}
                              </span>
                            ) : (
                              // Other users - show editable dropdown
                              <Select 
                                value={u.role} 
                                onValueChange={(newRole) => handleChangeRole(u.id, newRole, u.name)}
                                disabled={changingRoleFor === u.id}
                              >
                                <SelectTrigger className="w-40 h-8 text-xs">
                                  <SelectValue>
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                      u.role === "admin" ? "bg-purple-100 text-purple-700" :
                                      u.role === "rn" ? "bg-blue-100 text-blue-700" :
                                      u.role === "case_manager" ? "bg-emerald-100 text-emerald-700" :
                                      u.role === "intake_coordinator" ? "bg-amber-100 text-amber-700" :
                                      u.role === "receptionist" ? "bg-pink-100 text-pink-700" :
                                      "bg-slate-100 text-slate-700"
                                    }`}>
                                      {formatRoleName(u.role)}
                                    </span>
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="admin">
                                    <span className="flex items-center gap-2">
                                      <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                                      Administrator
                                    </span>
                                  </SelectItem>
                                  <SelectItem value="case_manager">
                                    <span className="flex items-center gap-2">
                                      <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                                      Case Manager
                                    </span>
                                  </SelectItem>
                                  <SelectItem value="hr_scheduler">
                                    <span className="flex items-center gap-2">
                                      <span className="w-2 h-2 rounded-full bg-violet-500"></span>
                                      HR/Scheduler
                                    </span>
                                  </SelectItem>
                                  <SelectItem value="evv_specialist">
                                    <span className="flex items-center gap-2">
                                      <span className="w-2 h-2 rounded-full bg-cyan-500"></span>
                                      EVV Specialist
                                    </span>
                                  </SelectItem>
                                  <SelectItem value="intake_coordinator">
                                    <span className="flex items-center gap-2">
                                      <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                                      Intake Coordinator
                                    </span>
                                  </SelectItem>
                                  <SelectItem value="receptionist">
                                    <span className="flex items-center gap-2">
                                      <span className="w-2 h-2 rounded-full bg-pink-500"></span>
                                      Receptionist
                                    </span>
                                  </SelectItem>
                                  <SelectItem value="rn">
                                    <span className="flex items-center gap-2">
                                      <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                                      Registered Nurse
                                    </span>
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                          </TableCell>
                          <TableCell>
                            {u.is_active ? (
                              <span className="flex items-center gap-1 text-emerald-600 text-sm">
                                <CheckCircle className="w-4 h-4" /> Active
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-slate-400 text-sm">
                                <XCircle className="w-4 h-4" /> Inactive
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {u.is_active ? (
                              u.billing_status === "included" ? (
                                <span className="text-xs text-emerald-600 font-medium">Included</span>
                              ) : (
                                <span className="text-xs text-amber-600 font-medium">+${u.monthly_cost?.toFixed(2)}/mo</span>
                              )
                            ) : (
                              <span className="text-xs text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {u.id !== user?.id && (
                              u.is_active ? (
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => handleDeactivateUser(u.id, u.name)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <UserMinus className="w-4 h-4 mr-1" />
                                  Deactivate
                                </Button>
                              ) : (
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => handleReactivateUser(u.id, u.name)}
                                  className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                >
                                  <UserPlus className="w-4 h-4 mr-1" />
                                  Reactivate
                                </Button>
                              )
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {usersSummary?.additional_users > 0 && (
                  <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                    <strong>Note:</strong> You have {usersSummary.additional_users} user(s) beyond your plan&apos;s included {usersSummary.included_users} users. 
                    This adds ${usersSummary.additional_users_cost?.toFixed(2)} to your monthly bill.
                  </div>
                )}
              </div>

              {/* Role Permissions */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Role Permissions</h2>
                    <p className="text-sm text-slate-500">Control what each role can access and do</p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowComparisonMatrix(true)}
                      className="gap-2"
                      data-testid="view-matrix-btn"
                    >
                      <Users className="w-4 h-4" />
                      Compare Roles
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleResetPermissions}
                      className="gap-2"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Reset to Defaults
                    </Button>
                  </div>
                </div>

                {permissions && (
                  <div className="space-y-4">
                    {["admin", "case_manager", "intake_coordinator", "receptionist", "rn", "pcw"].map((role) => (
                      <div key={role} className="border border-slate-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                              role === "admin" ? "bg-purple-100 text-purple-700" :
                              role === "rn" ? "bg-blue-100 text-blue-700" :
                              role === "case_manager" ? "bg-emerald-100 text-emerald-700" :
                              role === "intake_coordinator" ? "bg-amber-100 text-amber-700" :
                              role === "receptionist" ? "bg-pink-100 text-pink-700" :
                              "bg-slate-100 text-slate-700"
                            }`}>
                              {formatRoleName(role)}
                            </span>
                            {permissions.is_custom && <span className="text-xs text-amber-600">(customized)</span>}
                          </div>
                          {role !== "admin" && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => setEditingRole(editingRole === role ? null : role)}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              {editingRole === role ? "Close" : "Edit"}
                            </Button>
                          )}
                        </div>

                        {editingRole === role && permissions.permissions[role] && (
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-4 pt-4 border-t">
                            {Object.entries(permissions.permissions[role]).map(([feature, perms]) => (
                              <div key={feature} className="text-sm">
                                <p className="font-medium text-slate-700 capitalize mb-1">{feature.replace("_", " ")}</p>
                                {typeof perms === "boolean" ? (
                                  <div className="flex items-center gap-2">
                                    <Switch
                                      checked={perms}
                                      onCheckedChange={(checked) => {
                                        const updated = {...permissions.permissions[role]};
                                        updated[feature] = checked;
                                        handleSavePermissions(role, updated);
                                      }}
                                    />
                                    <span className="text-xs text-slate-500">{perms ? "Enabled" : "Disabled"}</span>
                                  </div>
                                ) : (
                                  <div className="space-y-1">
                                    {Object.entries(perms).map(([action, allowed]) => (
                                      <div key={action} className="flex items-center gap-2">
                                        <Switch
                                          checked={allowed}
                                          onCheckedChange={(checked) => {
                                            const updated = {...permissions.permissions[role]};
                                            updated[feature] = {...perms, [action]: checked};
                                            handleSavePermissions(role, updated);
                                          }}
                                        />
                                        <span className="text-xs text-slate-500 capitalize">{action}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {editingRole !== role && (
                          <div className="flex flex-wrap gap-2">
                            {permissions.permissions[role] && Object.entries(permissions.permissions[role]).map(([feature, perms]) => {
                              const hasAccess = typeof perms === "boolean" ? perms : Object.values(perms).some(v => v);
                              return (
                                <span 
                                  key={feature} 
                                  className={`px-2 py-0.5 rounded text-xs capitalize ${
                                    hasAccess ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"
                                  }`}
                                >
                                  {hasAccess ? <Check className="w-3 h-3 inline mr-1" /> : <X className="w-3 h-3 inline mr-1" />}
                                  {feature.replace("_", " ")}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Activity Log */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Activity Log</h2>
                    <p className="text-sm text-slate-500">Track user actions and changes</p>
                  </div>
                  <div className="flex gap-2 items-center">
                    <Select value={activityFilter} onValueChange={(v) => { setActivityFilter(v); }}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Filter" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Activities</SelectItem>
                        <SelectItem value="login">Logins</SelectItem>
                        <SelectItem value="user_invited">User Invites</SelectItem>
                        <SelectItem value="user_deactivated">Deactivations</SelectItem>
                        <SelectItem value="user_reactivated">Reactivations</SelectItem>
                        <SelectItem value="role_changed">Role Changes</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="outline" size="sm" onClick={() => handleExportActivityLogs("csv")}>
                      <Download className="w-4 h-4 mr-1" />
                      Export CSV
                    </Button>
                  </div>
                </div>

                {activityLogs.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No activity logs yet</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {activityLogs.map((log) => (
                      <div key={log.id} className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 border border-slate-100">
                        <span className="text-xl">{getActivityIcon(log.action_type)}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900">{log.action}</p>
                          <p className="text-xs text-slate-500">
                            by {log.user_name} • {formatActivityTime(log.timestamp)}
                          </p>
                          {log.details && Object.keys(log.details).length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {Object.entries(log.details).map(([key, value]) => (
                                <span key={key} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                                  {key}: {value}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <span className="text-xs text-slate-400 whitespace-nowrap">
                          {new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-4 w-full"
                  onClick={fetchActivityLogs}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh Logs
                </Button>
              </div>

              {/* Activity Notification Settings */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Activity Notifications</h2>
                    <p className="text-sm text-slate-500">Get email alerts for important user actions (MOCKED)</p>
                  </div>
                  {activityNotifSettings && (
                    <Switch
                      checked={activityNotifSettings.enabled}
                      onCheckedChange={(checked) => setActivityNotifSettings(prev => ({ ...prev, enabled: checked }))}
                    />
                  )}
                </div>

                {activityNotifSettings && activityNotifSettings.enabled && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="text-sm">User Logins</span>
                        <Switch
                          checked={activityNotifSettings.notify_on_login}
                          onCheckedChange={(checked) => setActivityNotifSettings(prev => ({ ...prev, notify_on_login: checked }))}
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="text-sm">User Invited</span>
                        <Switch
                          checked={activityNotifSettings.notify_on_user_invited}
                          onCheckedChange={(checked) => setActivityNotifSettings(prev => ({ ...prev, notify_on_user_invited: checked }))}
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="text-sm">User Deactivated</span>
                        <Switch
                          checked={activityNotifSettings.notify_on_user_deactivated}
                          onCheckedChange={(checked) => setActivityNotifSettings(prev => ({ ...prev, notify_on_user_deactivated: checked }))}
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="text-sm">User Reactivated</span>
                        <Switch
                          checked={activityNotifSettings.notify_on_user_reactivated}
                          onCheckedChange={(checked) => setActivityNotifSettings(prev => ({ ...prev, notify_on_user_reactivated: checked }))}
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="text-sm">Role Changed</span>
                        <Switch
                          checked={activityNotifSettings.notify_on_role_changed}
                          onCheckedChange={(checked) => setActivityNotifSettings(prev => ({ ...prev, notify_on_role_changed: checked }))}
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="text-sm">Failed Logins</span>
                        <Switch
                          checked={activityNotifSettings.notify_on_failed_login}
                          onCheckedChange={(checked) => setActivityNotifSettings(prev => ({ ...prev, notify_on_failed_login: checked }))}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Notification Email(s)</Label>
                      <Input
                        value={activityNotifSettings.notification_emails?.join(", ") || ""}
                        onChange={(e) => setActivityNotifSettings(prev => ({ 
                          ...prev, 
                          notification_emails: e.target.value.split(",").map(s => s.trim()).filter(Boolean)
                        }))}
                        placeholder="admin@example.com, manager@example.com"
                      />
                      <p className="text-xs text-slate-500">Separate multiple emails with commas</p>
                    </div>

                    <Button onClick={handleSaveNotifSettings} disabled={savingNotifSettings} className="w-full">
                      {savingNotifSettings ? "Saving..." : "Save Notification Settings"}
                    </Button>
                  </div>
                )}

                {activityNotifSettings && !activityNotifSettings.enabled && (
                  <p className="text-sm text-slate-500 text-center py-4">
                    Activity notifications are disabled. Toggle the switch above to enable them.
                  </p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Staff Permissions Tab */}
          <TabsContent value="permissions">
            <StaffPermissionsTab />
          </TabsContent>

          {/* HR Settings Tab */}
          <TabsContent value="hr">
            <HRSettingsTab
              settings={evalReminderSettings}
              onChange={setEvalReminderSettings}
              onSave={handleSaveEvalReminderSettings}
              saving={savingEvalReminders}
            />
          </TabsContent>

          {/* Service Codes Tab */}
          <TabsContent value="service-codes">
            <ServiceCodesSettingsTab />
          </TabsContent>

          {/* Integrations Tab */}
          <TabsContent value="integrations">
            <IntegrationsSettingsTab />
          </TabsContent>

          <TabsContent value="fax-templates">
            <FaxTemplatesSettingsTab />
          </TabsContent>

          <TabsContent value="orientation-kit">
            <OrientationKitTab isAdmin={user?.role === "admin"} />
          </TabsContent>

          <TabsContent value="form-templates">
            <FormTemplatesTab />
          </TabsContent>

          <TabsContent value="visit-reminders">
            <VisitRemindersTab />
          </TabsContent>

          {/* Help & Support Tab - Using extracted component */}
          <TabsContent value="help">
            <HelpSupportTab />
          </TabsContent>
        </Tabs>
      )}

      {/* Extracted Dialog Components */}
      <CreateTemplateDialog
        open={showCreateTemplate}
        onOpenChange={setShowCreateTemplate}
        newTemplate={newTemplate}
        setNewTemplate={setNewTemplate}
        saving={savingTemplate}
        onSubmit={handleCreateTemplate}
        togglePermission={toggleTemplatePermission}
      />

      <EditTemplateDialog
        editingTemplate={editingTemplate}
        setEditingTemplate={setEditingTemplate}
        saving={savingTemplate}
        onSubmit={handleUpdateTemplate}
        togglePermission={toggleTemplatePermission}
      />

      <PasswordChangeDialog
        open={passwordDialog}
        onOpenChange={setPasswordDialog}
        passwordData={passwordData}
        setPasswordData={setPasswordData}
        changing={changingPassword}
        onSubmit={handleChangePassword}
      />

      {/* Invite User Dialog - Using extracted component */}
      <InviteUserDialog
        open={inviteDialog}
        onOpenChange={setInviteDialog}
        inviteData={inviteData}
        setInviteData={setInviteData}
        billingPreview={billingPreview}
        inviting={inviting}
        onInvite={handleInviteUser}
      />

      {/* Bulk Import Dialog - Using extracted component */}
      <BulkImportDialog
        open={importDialog}
        onOpenChange={setImportDialog}
        csvData={csvData}
        setCsvData={setCsvData}
        importPreview={importPreview}
        setImportPreview={setImportPreview}
        importResult={importResult}
        importing={importing}
        onDownloadTemplate={handleDownloadTemplate}
        onPreviewImport={handlePreviewImport}
        onExecuteImport={handleExecuteImport}
      />

      <RoleComparisonMatrixDialog
        open={showComparisonMatrix}
        onOpenChange={setShowComparisonMatrix}
        permissions={permissions}
      />

      {/* Terms & Conditions Modal */}
      <TermsModal
        open={termsModalOpen}
        onOpenChange={setTermsModalOpen}
        onAccept={proceedToCheckout}
        planName={selectedPlanForUpgrade?.name}
      />
    </Layout>
  );
}
